import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { confirmBillCreate } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { useCreateIpdBill, useIpdPatientCreditBills } from '../ipd-daycare/ipd-daycare.queries';
import { currency, dateTimeInput } from '../ipd-daycare/ipd-daycare.utils';
import type { IpdBillPaymentInput, IpdBillPaymentMode } from '../ipd-daycare/types';
import { useOtLookup } from './ot.queries';
import { FieldLabel, LookupSelect } from './ot.shared';
import type { OtCase } from './types';

interface DraftCharge { charge: string; name: string; doctor: string | null; rate: number; quantity: number; discountPercent: number; discountAmount: number }

const lineAmount = (line: Pick<DraftCharge, 'rate' | 'quantity' | 'discountPercent' | 'discountAmount'>) => {
  const gross = line.rate * line.quantity;
  return Math.max(0, gross - Math.max(line.discountAmount, gross * line.discountPercent / 100));
};

const paymentModes: IpdBillPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

export const OtCreateBillForm = ({ admission, onCreated }: { admission: OtCase; onCreated: (billId: string) => void }) => {
  const { showToast } = useToast();
  const charges = useOtLookup('charges');
  const packages = useOtLookup('packages');
  const doctors = useOtLookup('doctors');
  const creditBills = useIpdPatientCreditBills(admission.patient.id);
  const createBill = useCreateIpdBill(admission.id);

  const [billDate, setBillDate] = useState(dateTimeInput);
  const [lines, setLines] = useState<DraftCharge[]>([]);
  const [chargeId, setChargeId] = useState('');
  const [draftDoctor, setDraftDoctor] = useState<string | null>(null);
  const [draftRate, setDraftRate] = useState(0);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftDiscountPercent, setDraftDiscountPercent] = useState(0);
  const [draftDiscountAmount, setDraftDiscountAmount] = useState(0);
  const [packageId, setPackageId] = useState('');

  const [payment, setPayment] = useState<IpdBillPaymentInput>({ mode: 'Cash', bankName: null, amount: 0 });
  const [creditUse, setCreditUse] = useState<Record<string, number>>({});

  const [miscChargeType, setMiscChargeType] = useState<'flat' | 'percentage'>('flat');
  const [miscCharge, setMiscCharge] = useState(0);
  const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('flat');
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [financeRemark, setFinanceRemark] = useState('');
  const [departmentPartyRemark, setDepartmentPartyRemark] = useState('');

  const subTotal = useMemo(() => lines.reduce((sum, line) => sum + lineAmount(line), 0), [lines]);
  const miscAmount = miscChargeType === 'percentage' ? subTotal * miscCharge / 100 : miscCharge;
  const total = subTotal + miscAmount;
  const discountAmount = discountType === 'percentage' ? total * totalDiscount / 100 : totalDiscount;
  const grandTotal = Math.max(0, total - discountAmount);
  const paymentsTotal = Number(payment.amount || 0);
  const creditApplied = Object.values(creditUse).reduce((sum, amount) => sum + Number(amount || 0), 0);
  const totalPayment = paymentsTotal + creditApplied;
  const dueAmount = Math.max(0, grandTotal - totalPayment);
  const creditAmount = Math.max(0, totalPayment - grandTotal);

  const resetDraft = () => { setChargeId(''); setDraftDoctor(null); setDraftRate(0); setDraftQuantity(1); setDraftDiscountPercent(0); setDraftDiscountAmount(0); };
  const chooseCharge = (id: string) => {
    setChargeId(id);
    const selected = charges.data?.find((item) => item.id === id);
    setDraftRate(selected?.rate ?? 0);
    setDraftQuantity(1);
    setDraftDiscountPercent(0);
    setDraftDiscountAmount(0);
  };
  const draftAmount = lineAmount({ rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount });
  const addCharge = () => {
    const selected = charges.data?.find((item) => item.id === chargeId);
    if (!selected) return;
    setLines((current) => [...current, { charge: selected.id, name: selected.label, doctor: draftDoctor, rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount }]);
    resetDraft();
  };
  const removeLine = (index: number) => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index));
  const updateLine = (index: number, patch: Partial<DraftCharge>) => setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));

  const applyPackage = (id: string) => {
    setPackageId(id);
    const selected = packages.data?.find((item) => item.id === id);
    const items = selected?.items;
    if (!items) return;
    setLines((current) => [
      ...current,
      ...items.map((item) => ({ charge: item.charge, name: item.chargeName, doctor: null, rate: item.rate, quantity: 1, discountPercent: 0, discountAmount: 0 })),
    ]);
  };

  const setCreditAmount = (billId: string, max: number, value: number) => setCreditUse((current) => ({ ...current, [billId]: Math.min(Math.max(0, value), max) }));

  const canSubmit = lines.length > 0 && !createBill.isPending;

  const submit = () => {
    if (!canSubmit) return;
    void confirmBillCreate().then((confirmed) => {
      if (!confirmed) return;
      createBill.mutate({
        billDate,
        charges: lines.map((line) => ({ charge: line.charge, doctor: line.doctor, date: billDate, rate: line.rate, quantity: line.quantity, discountPercent: line.discountPercent, discountAmount: line.discountAmount })),
        miscChargeType,
        miscCharge,
        discountType,
        totalDiscount,
        payments: [payment].filter((entry) => entry.amount > 0),
        creditAdjustments: Object.entries(creditUse).filter(([, amount]) => amount > 0).map(([sourceBill, amount]) => ({ sourceBill, amount })),
        financeRemark: financeRemark.trim() || null,
        departmentPartyRemark: departmentPartyRemark.trim() || null,
      }, {
        onSuccess: (bill) => { showToast('Bill created successfully.', 'success'); onCreated(bill.id); },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The bill could not be created.', 'error'),
      });
    });
  };

  return (
    <div className="opd-prescription">
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>Create New Bill — {admission.patientSnapshot.name}</span></div>
        <p>OT No {admission.otNumber}</p>
      </header>

      <section className="opd-section">
        <div className="opd-edit-bill-fields">
          <label><FieldLabel required>Billing Date</FieldLabel><input type="datetime-local" value={billDate} onChange={(event) => setBillDate(event.target.value)} /></label>
          <label><FieldLabel>Package</FieldLabel><LookupSelect id="ot-create-bill-package" value={packageId} options={packages.data} placeholder="Select Package" onChange={applyPackage} /></label>
        </div>
      </section>

      <section className="opd-section"><h2>Charges</h2>
        <div className="table-scroll">
          <table className="opd-charge-table">
            <thead><tr><th>Charge Name<span className="opd-required">*</span></th><th>Reffer Doctor</th><th>Rate (₹)<span className="opd-required">*</span></th><th className="opd-charge-narrow">#</th><th>Qty<span className="opd-required">*</span></th><th>Dis (%)</th><th>Dis Amount (₹)</th><th>Amount (₹)</th><th /></tr></thead>
            <tbody>
              <tr className="opd-draft-row">
                <td><LookupSelect id="ot-create-bill-charge" value={chargeId} options={charges.data} placeholder="Select" onChange={chooseCharge} /></td>
                <td><LookupSelect id="ot-create-bill-doctor" value={draftDoctor ?? ''} options={doctors.data} placeholder="Select" onChange={(value) => setDraftDoctor(value || null)} /></td>
                <td><input type="number" min={0} step="0.01" value={draftRate} onChange={(event) => setDraftRate(Number(event.target.value))} /></td>
                <td className="opd-charge-narrow"><button className="opd-add" type="button" onClick={addCharge} disabled={!chargeId}>+</button></td>
                <td><input type="number" min={1} value={draftQuantity} onChange={(event) => setDraftQuantity(Number(event.target.value))} /></td>
                <td><input type="number" min={0} max={100} step="0.01" value={draftDiscountPercent} onChange={(event) => setDraftDiscountPercent(Number(event.target.value))} /></td>
                <td><input type="number" min={0} step="0.01" value={draftDiscountAmount} onChange={(event) => setDraftDiscountAmount(Number(event.target.value))} /></td>
                <td><strong>{currency(draftAmount)}</strong></td>
                <td />
              </tr>
              {lines.length === 0 ? <tr><td className="opd-bill-empty" colSpan={9}>Add at least one charge to create a bill.</td></tr> : null}
              {lines.map((line, index) => (
                <tr key={index}>
                  <td>{line.name}</td>
                  <td><LookupSelect id={`ot-create-bill-line-doctor-${index}`} value={line.doctor ?? ''} options={doctors.data} placeholder="Select" onChange={(value) => updateLine(index, { doctor: value || null })} /></td>
                  <td><input type="number" min={0} step="0.01" value={line.rate} onChange={(event) => updateLine(index, { rate: Number(event.target.value) })} /></td>
                  <td className="opd-charge-narrow" />
                  <td><input type="number" min={1} value={line.quantity} onChange={(event) => updateLine(index, { quantity: Number(event.target.value) })} /></td>
                  <td><input type="number" min={0} max={100} step="0.01" value={line.discountPercent} onChange={(event) => updateLine(index, { discountPercent: Number(event.target.value) })} /></td>
                  <td><input type="number" min={0} step="0.01" value={line.discountAmount} onChange={(event) => updateLine(index, { discountAmount: Number(event.target.value) })} /></td>
                  <td><strong>{currency(lineAmount(line))}</strong></td>
                  <td><button className="opd-cancel" type="button" onClick={() => removeLine(index)} aria-label={`Remove ${line.name}`}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(creditBills.data ?? []).length > 0 ? (
        <section className="opd-bill-subsection">
          <h3>Available Credit</h3>
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>Bill No</th><th>Available Credit</th><th>Adjust Amount</th></tr></thead>
              <tbody>
                {(creditBills.data ?? []).map((option) => (
                  <tr key={option.id}>
                    <td>{option.billNumber}</td>
                    <td>{currency(option.creditAmount)}</td>
                    <td><input type="number" min={0} max={option.creditAmount} step="0.01" value={creditUse[option.id] ?? 0} onChange={(event) => setCreditAmount(option.id, option.creditAmount, Number(event.target.value))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="opd-section opd-payment-section"><h2>Payment</h2>
        <div className="opd-payment-row">
          <div className="opd-payment-methods">
            <div className="opd-payment-inline">
              <label>Mode:
                <select value={payment.mode} onChange={(event) => setPayment((current) => ({ ...current, mode: event.target.value as IpdBillPaymentMode }))}>{paymentModes.map((mode) => <option key={mode}>{mode}</option>)}</select>
              </label>
              <label>Bank:
                <input value={payment.bankName ?? ''} onChange={(event) => setPayment((current) => ({ ...current, bankName: event.target.value || null }))} />
              </label>
              <label>Amount:
                <input type="number" min={0} step="0.01" value={payment.amount} onChange={(event) => setPayment((current) => ({ ...current, amount: Number(event.target.value) }))} />
              </label>
            </div>
          </div>
          <div className="opd-total-panel">
            <div><span>Sub Total</span><strong>{currency(subTotal)}</strong></div>
            <div><span>Miscellaneous</span><input type="number" min={0} step="0.01" value={miscCharge} onChange={(event) => setMiscCharge(Number(event.target.value))} /><select value={miscChargeType} onChange={(event) => setMiscChargeType(event.target.value as 'flat' | 'percentage')}><option value="flat">Rs.</option><option value="percentage">%</option></select></div>
            <div><span>Total</span><strong>{currency(total)}</strong></div>
            <div><span>Discount</span><input type="number" min={0} step="0.01" value={totalDiscount} onChange={(event) => setTotalDiscount(Number(event.target.value))} /><select value={discountType} onChange={(event) => setDiscountType(event.target.value as 'flat' | 'percentage')}><option value="flat">Rs.</option><option value="percentage">%</option></select></div>
            <div><span>Grand Total</span><strong>{currency(grandTotal)}</strong></div>
            <div><span>Total Payment</span><strong>{currency(totalPayment)}</strong></div>
            <div><span>Due / Credit</span><strong className={dueAmount > 0 ? 'due' : undefined}>{dueAmount > 0 ? `Due ${currency(dueAmount)}` : `Credit ${currency(creditAmount)}`}</strong></div>
          </div>
        </div>
      </section>

      <div className="opd-edit-bill-footer-row">
        <div className="opd-edit-bill-remarks">
          <label><CkEditorField label="Finance Remarks" value={financeRemark} onChange={setFinanceRemark} /></label>
          <label><CkEditorField label="Party/Department Remarks" value={departmentPartyRemark} onChange={setDepartmentPartyRemark} /></label>
        </div>
        <div className="opd-edit-bill-pay-summary">
          <div className="pay"><span>Total Pay (₹)</span><strong>{currency(totalPayment)}</strong></div>
          <div className="due"><span>Total Due (₹)</span><strong>{currency(dueAmount)}</strong></div>
          {creditAmount > 0 ? <div><span>Credit (₹)</span><strong>{currency(creditAmount)}</strong></div> : null}
        </div>
      </div>

      <div className="opd-form-actions">
        <button className="button button-primary" type="button" disabled={!canSubmit} onClick={submit}>{createBill.isPending ? 'Saving…' : 'Bill Save in Draft'}</button>
      </div>
    </div>
  );
};
